import { Component, OnInit, Input, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { Unsubscribable, BehaviorSubject, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

import {
  IAlertType,
  IApplicationAlertSeverity,
  IAlertListRow,
  EntityCacheProvider,
  TransformationService,
  ValuesExchangeProvider,
  UserService,
  UserAuth,
  PaginatorComponent
} from '@nrwl/common-lib';

import { ProfileSettingsUpdater } from '../../../profile-settings-updater';
import { WizardNavigatorService } from '../../../wizard-navigator.service';

@Component({
  selector: 'nrwl-application-alerts',
  templateUrl: './application-alerts.component.html',
  styleUrls: ['./application-alerts.component.scss']
})
export class ApplicationAlertsComponent
  extends ProfileSettingsUpdater
  implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('paginator', { static: true }) paginatorComponent: PaginatorComponent;
  // eslint-disable-next-line
  @Input() dataSource: BehaviorSubject<any>;
  appsdisplayedColumns: string[] = ['application', 'minimumseverity', 'remove'];
  minimumSeverity: string[];
  // eslint-disable-next-line
  appMinimumSeverity: any;
  showAppAddList = true;
  appSelectAll = false;
  appAlertsList: IAlertListRow[] = [];
  // eslint-disable-next-line
  userDefinedAppData: MatTableDataSource<any>;
  defaultAppChecked = false;
  // eslint-disable-next-line
  alertPreferenceData: any;
  selectAppsUnsubscribable: Unsubscribable;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly entityCache: EntityCacheProvider,
    private readonly exchange: ValuesExchangeProvider,
    private readonly transformService: TransformationService,
    private readonly wizardNavigatorService: WizardNavigatorService,
    userService: UserService,
    activatedRoute: ActivatedRoute,
    userAuth: UserAuth
  ) {
    super(userService, activatedRoute, userAuth);
  }

  ngOnInit() {
    // eslint-disable-next-line
    this.userDefinedAppData = new MatTableDataSource<any>([]);
    this.minimumSeverity = ['warning', 'critical'];
    this.appMinimumSeverity = 'warning';
    this.loadNotificationPreference();
    this.setCustomApps();
    this.selectAppsUnsubscribable = this.exchange.registerInterest('selectedApps', selectedApps => {
      if (selectedApps) {
        selectedApps.forEach(app => this.appAlertsList.push(app));
        this.userDefinedAppData.data = this.appAlertsList;
        this._setApplicationAlerts();
        this._saveAlerts();
      }
    });
  }

  ngAfterViewInit(): void {
    this.userDefinedAppData.paginator = this.paginatorComponent.paginator;
  }

  nextPage(event: PageEvent): void {
    this.userDefinedAppData.paginator.pageIndex = event.pageIndex;
  }

  ngOnDestroy(): void {
    this.exchange.notifyListeners('selectedApps', null);
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribeUpdates();
    if (this.selectAppsUnsubscribable) {
      this.selectAppsUnsubscribable.unsubscribe();
    }
  }
  // eslint-disable-next-line
  openAppSelectionWizard(wizardId): void {
    this.exchange.notifyListeners('apps-filter', this.appAlertsList);
    this.wizardNavigatorService.navigateToWizard(wizardId);
  }

  removeApp(appRow: IAlertListRow): void {
    const index = this.appAlertsList.findIndex(app => app.appId === appRow.appId);
    this.appAlertsList.splice(index, 1);
    this._setApplicationAlerts();
    this._saveAlerts();
    this.updateTableDataSource();
  }

  setCustomApps(): void {
    this.dataSource.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      const appAlertsList = this.entityCache.findEntityCache('app').getValues();
      const userDefinedApps = appAlertsList.filter(app => app.isUserDefined());
      const filterappAlertsList = this.transformService.transformData(
        'user-application-management',
        userDefinedApps
      );
      if (data.performanceAlertConfiguration) {
        const alerts: IApplicationAlertSeverity[] =
          data.performanceAlertConfiguration.performanceAlertsByApplications.customApplications;
        alerts?.forEach(alert => {
          const app = filterappAlertsList.data.find(a => a.id === alert.appId);
          this.appAlertsList.push({
            appId: alert.appId,
            minimumSeverity: alert.minimumSeverity,
            name: app.name
          });
        });
      }
      this.userDefinedAppData.data = this.appAlertsList;
    });
  }

  // eslint-disable-next-line
  minimumSeverityChange(event, value, row?: IAlertListRow): void {
    if (value === 'allApp') {
      this.appMinimumSeverity = event.value;
    } else if (value === 'customAppSeverity' && row) {
      const app = this.appAlertsList.find(a => a.appId === row.appId);
      if (app) {
        app.minimumSeverity = event.value;
      }
    }

    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertsByApplications.allApplications.minimumSeverity = this.appMinimumSeverity;
    this._setApplicationAlerts();
    this._saveAlerts();
  }

  // eslint-disable-next-line
  changeApplication(event): void {
    switch (event.value) {
      case 'application': {
        this.showAppAddList = false;
        this.appSelectAll = true;
        this.appAlertsList = [];
        this.userDefinedAppData.data = [];
        break;
      }
      case 'custom-application': {
        this.showAppAddList = true;
        this.appSelectAll = false;
        break;
      }
    }

    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertsByApplications.allApplications.selectAll = this.appSelectAll;
    this._setApplicationAlerts();
    this._saveAlerts();
  }

  updateTableDataSource(): void {
    this.userDefinedAppData.data = this.appAlertsList;
  }

  private _saveAlerts() {
    this.updateUserAlertsPreferences(this.alertPreferenceData);
  }

  private _setApplicationAlerts() {
    const alerts: IApplicationAlertSeverity[] = this.appAlertsList.map(app => {
      return {
        appId: app.appId,
        minimumSeverity: app.minimumSeverity,
        alertType: IAlertType.APP
      } as IApplicationAlertSeverity;
    });
    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertsByApplications.customApplications = alerts;
  }

  private loadNotificationPreference() {
    this.dataSource.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      this.alertPreferenceData = data;
      const performanceAlertConfiguration = this.alertPreferenceData.performanceAlertConfiguration;
      this.appMinimumSeverity =
        performanceAlertConfiguration.performanceAlertsByApplications.allApplications.minimumSeverity;
      this.appSelectAll =
        performanceAlertConfiguration.performanceAlertsByApplications.allApplications.selectAll;
      this.defaultAppChecked = this.appSelectAll;
      this.showAppAddList = !this.appSelectAll;
    });
  }
}
